// Toppers site footer rendered in-tree by PageSkeleton.
//
// The footer navigation is serialized from the "footer" NavMenu in
// chrome._build_footer_nav_columns (mirroring how the header serializes
// top_nav_menu) and rendered here as a real React tree: the always-on contact
// bar (phone + Email Us) at the top, the desktop `.footer-nav` and the mobile
// `.footer-nav-mobile` accordion. The a360 badge, EmailSignupFooter island and
// footer-legal render below it.
//
// The mobile accordion blocks own their own open/close state: clicking a
// `.footer-nav-mobile__header` flips `footer-nav-mobile__block--active` on its
// parent `.footer-nav-mobile__block`. The header is a `<div>`, so it is exposed
// as a keyboard-operable `role="button"` with `aria-expanded`; blocks start
// closed.
import { Immutable } from "immer";
import React, { useState } from "react";
import { t } from "ttag";

import { Context } from "@reactivated";

import { EmailSignupFooter } from "../EmailSignupFooter";

// Mutable reactivated props (not Immutable<>) to match the PageSkeleton chain.
// The footer now lives on GlobalChrome (the `chrome` context processor), so its
// type is derived from the reactivated Context rather than a page @template.
type IFooter = React.ContextType<typeof Context>["chrome"]["footer"];

// A single ordered footer-nav sub_nav item: a link block or a raw-HTML block
// (e.g. the OneTrust cookie-settings button), interleaved in stream order.
type IFooterNavCategory =
    IFooter["footer_nav_columns"][number]["categories"][number];
type IFooterNavItem = IFooterNavCategory["items"][number];

type IProps = {
    footer: IFooter;
};

// Desktop `.footer-nav` sub_nav item. Every item carries the
// `w-block-{type} block-{type}` wrapper that Wagtail's `render_basic` emitted
// around each `{{ self.sub_nav }}` child. A raw block injects its server render
// (already `<div class="raw-html-block">...`) directly into that wrapper; a link
// block renders the `footer-nav__item-block` anchor structure.
const FooterNavDesktopItem = (
    props: Immutable<{ item: IFooterNavItem }>,
): React.JSX.Element => {
    const { item } = props;
    const wblockClass = `w-block-${item.block_type} block-${item.block_type}`;
    if (item.is_raw) {
        return (
            <div
                className={wblockClass}
                dangerouslySetInnerHTML={{ __html: item.raw_html }}
            />
        );
    }
    return (
        <div className={wblockClass}>
            <div className="footer-nav__item-block">
                <div className="footer-nav__item">
                    <a
                        className="footer-nav__item-link"
                        href={item.url}
                        target={item.open_in_new_tab ? "_blank" : undefined}
                        rel={
                            item.open_in_new_tab
                                ? "noopener noreferrer"
                                : undefined
                        }
                    >
                        {item.title}
                    </a>
                </div>
            </div>
        </div>
    );
};

// Mobile `.footer-nav-mobile` sub_nav item. The old mobile markup was hand-built
// and UNWRAPPED (no `w-block-*`), so a raw block injects its server render
// directly. `display: contents` keeps the wrapper from adding a layout box, so
// the `raw-html-block` sits in `.site-footer__dropdown` exactly as the old
// `{{ sub_nav }}` render did.
const FooterNavMobileItem = (
    props: Immutable<{ item: IFooterNavItem }>,
): React.JSX.Element => {
    const { item } = props;
    if (item.is_raw) {
        return (
            <div
                style={{ display: "contents" }}
                dangerouslySetInnerHTML={{ __html: item.raw_html }}
            />
        );
    }
    return (
        <div className="footer-nav__item footer-nav__item--mobile">
            <a className="footer-nav__item--mobile-link" href={item.url}>
                {item.title}
            </a>
        </div>
    );
};

// A mobile footer-nav block: the `.footer-nav-mobile__header` toggles the
// block's `--active` state, which the SCSS uses to reveal the dropdown.
const FooterNavMobileBlock = (
    props: Immutable<{ category: IFooterNavCategory }>,
): React.JSX.Element => {
    const { category } = props;
    const [isOpen, setIsOpen] = useState(false);
    const toggle = () => {
        setIsOpen((open) => !open);
    };
    return (
        <div
            className={
                isOpen
                    ? "footer-nav-mobile__block footer-nav-mobile__block--active"
                    : "footer-nav-mobile__block"
            }
        >
            <div
                className="footer-nav-mobile__header site-footer__header"
                role="button"
                tabIndex={0}
                aria-expanded={isOpen ? "true" : "false"}
                onClick={toggle}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggle();
                    }
                }}
            >
                {category.title}
            </div>
            <div className="site-footer__dropdown">
                {category.items.map((item, itemIndex) => (
                    <FooterNavMobileItem key={itemIndex} item={item} />
                ))}
            </div>
        </div>
    );
};

export const SiteFooter = (props: IProps): React.JSX.Element => {
    const { footer } = props;
    // Evaluated at render scope so the locale is resolved per-request (toppers
    // serves fr_CA), not frozen at module load.
    const a360Alt = t`An Accessible360 badge sharing that we've partnered with an industry-leading ADA vendor who maintains our site is in good standing with accessibility.`;
    return (
        <footer className="site-footer l-full-width">
            <div className="site-footer__border" />

            {/*
             * The footer nav (contact bar + desktop `.footer-nav` + mobile
             * `.footer-nav-mobile`) rendered from the serialized "footer"
             * NavMenu. The wrapper div preserves the previous chrome DOM (the
             * three sections were siblings inside a single container div).
             */}
            <div>
                <div className="site-footer-contact">
                    <div className="l-capped-width">
                        <a
                            className="site-footer-contact__item site-footer-contact__phone"
                            href={`tel:${footer.footer_contact_phone}`}
                        >
                            <span className="footer-nav__icon footer-nav__icon--mobile footer-nav__phone-icon" />{" "}
                            {footer.footer_contact_phone}
                        </a>
                        <a
                            href={footer.contact_us_url}
                            className="site-footer-contact__item site-footer-contact__email"
                        >
                            <span className="footer-nav__icon footer-nav__icon--mobile footer-nav__email-icon" />
                            {t`Email Us`}
                        </a>
                    </div>
                </div>

                <nav className="l-capped-width site-footer__nav-container">
                    <div className="footer-nav">
                        {footer.footer_nav_columns.map(
                            (column, columnIndex) => (
                                <div
                                    key={columnIndex}
                                    className="footer-nav__col"
                                >
                                    <ul className="footer-nav__list">
                                        {column.categories.map(
                                            (category, categoryIndex) => (
                                                <React.Fragment
                                                    key={categoryIndex}
                                                >
                                                    <li
                                                        className="site-footer__header"
                                                        role="heading"
                                                        aria-level={2}
                                                    >
                                                        {category.title}
                                                    </li>
                                                    {category.items.map(
                                                        (item, itemIndex) => (
                                                            <FooterNavDesktopItem
                                                                key={itemIndex}
                                                                item={item}
                                                            />
                                                        ),
                                                    )}
                                                </React.Fragment>
                                            ),
                                        )}
                                    </ul>
                                </div>
                            ),
                        )}
                    </div>
                </nav>

                <nav className="footer-nav-mobile u-margin-bottom--xl">
                    {footer.footer_nav_columns.map((column, columnIndex) =>
                        column.categories.map((category, categoryIndex) => (
                            <FooterNavMobileBlock
                                key={`${columnIndex}-${categoryIndex}`}
                                category={category}
                            />
                        )),
                    )}
                </nav>
            </div>

            <div className="footer-badges l-capped-width">
                <div className="footer-badges__item footer-badges__item--a360">
                    <a
                        href="https://allyant.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <img
                            className="responsive-img"
                            src={footer.a360_badge_url}
                            alt={a360Alt}
                            loading="lazy"
                            decoding="async"
                        />
                    </a>
                </div>
            </div>

            <EmailSignupFooter />

            <div className="footer-legal l-capped-width">
                <div className="footer-legal__copyright">
                    <ul className="footer-legal__list">
                        <li className="footer-legal__item">
                            &copy; {footer.copyright_year} Tempur-Pedic North
                            America, LLC. {t`All Rights Reserved.`}
                        </li>
                        <br className="footer-legal__item-br" />
                        <li className="footer-legal__item">
                            <span className="footer-legal__item_divider footer-legal__item_divider--hide">
                                |
                            </span>
                            <a href={footer.privacy_policy_url}>
                                {t`Privacy Policy`}
                            </a>
                        </li>
                        <li className="footer-legal__item">
                            <span className="footer-legal__item_divider">
                                |
                            </span>
                            <a href={footer.terms_url}>{t`Terms of Use`}</a>
                        </li>
                    </ul>
                </div>
                <div className="footer-legal__aux-copy">
                    {footer.footer_copy.map((item, i) => (
                        <div
                            key={i}
                            dangerouslySetInnerHTML={{ __html: item.html }}
                        />
                    ))}
                </div>
            </div>
        </footer>
    );
};
